import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  useMediaQuery,
  InputAdornment,
} from '@mui/material';
import { Email, Phone, PhoneInTalk, Sms, Lock, CheckCircle } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import authService from '../../services/authService';

const steps = ['Choose Login', 'Verify OTP'];

type LoginMethod = 'email' | 'sms' | 'voice';

const extractDigits = (value: string): string => value.replace(/\D/g, '');

const validatePhoneNumber = (phone: string): boolean => {
  const digitsOnly = extractDigits(phone);
  return digitsOnly.length >= 10 && digitsOnly.length <= 15;
};

const validateEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const validateOTP = (value: string): boolean => /^\d{6}$/.test(value);

const formatPhoneNumber = (value: string): string => {
  const digitsOnly = extractDigits(value);

  if (digitsOnly.length <= 3) return digitsOnly;
  if (digitsOnly.length <= 6) return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3)}`;
  if (digitsOnly.length <= 10) {
    return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`;
  }
  return digitsOnly.replace(/(\d{3})(?=\d)/g, '$1 ');
};

const Login: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  
  const { login } = useAuth();
  
  const [activeStep, setActiveStep] = useState(0);
  const [loginMethod, setLoginMethod] = useState<LoginMethod>('email');
  const [identifier, setIdentifier] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const phoneDigits = useMemo(() => extractDigits(identifier), [identifier]);
  const normalizedIdentifier = useMemo(
    () => (loginMethod === 'email' ? identifier.trim() : phoneDigits),
    [identifier, loginMethod, phoneDigits]
  );
  const isIdentifierValid = useMemo(
    () => (loginMethod === 'email' ? validateEmail(identifier) : validatePhoneNumber(identifier)),
    [identifier, loginMethod]
  );
  const isOTPValid = useMemo(() => validateOTP(otp), [otp]);

  const deliveryLabel = loginMethod === 'email' ? 'email' : loginMethod === 'sms' ? 'text message' : 'phone call';
  const identifierLabel = loginMethod === 'email' ? 'Email Address' : 'Phone Number';
  const identifierPlaceholder = loginMethod === 'email' ? 'name@example.com' : '(123) 456-7890';
  const invalidIdentifierMessage = loginMethod === 'email'
    ? 'Please enter a valid email address'
    : 'Please enter a valid phone number (10-15 digits)';

  const handleLoginMethodChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, nextMethod: LoginMethod | null) => {
      if (!nextMethod || nextMethod === loginMethod) return;
      setLoginMethod(nextMethod);
      setIdentifier('');
      setOtp('');
      setError('');
      setSuccess('');
    },
    [loginMethod]
  );

  const handleSendOTP = useCallback(async () => {
    if (!isIdentifierValid) {
      setError(invalidIdentifierMessage);
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await authService.sendOTP(normalizedIdentifier, {
        authorizeOtp: loginMethod !== 'email',
        call: loginMethod === 'voice',
      });
      if (response.success) {
        setSuccess(`OTP sent successfully. Check your ${deliveryLabel}.`);
        setActiveStep(1);
      } else {
        setError(response.message || 'Failed to send OTP');
      }
    } catch (err) {
      setError('Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [deliveryLabel, invalidIdentifierMessage, isIdentifierValid, loginMethod, normalizedIdentifier]);

  const handleVerifyOTP = useCallback(async () => {
    if (!validateOTP(otp)) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const isLoginSuccessful = await login(normalizedIdentifier, otp);

      if (isLoginSuccessful) {
        setSuccess('Login successful!');
        // Navigation will be handled by the router based on auth state
      } else {
        setError('Invalid OTP. Please try again.');
      }
    } catch (err) {
      setError('Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [login, normalizedIdentifier, otp]);

  const handleIdentifierChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setIdentifier(loginMethod === 'email' ? value : formatPhoneNumber(value));
    setError('');
    setSuccess('');
  }, [loginMethod]);

  const handleOTPChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtp(value);
    setError('');
    setSuccess('');
  }, []);

  const handleOtpKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && !loading && isOTPValid) {
        handleVerifyOTP();
      }
    },
    [handleVerifyOTP, isOTPValid, loading]
  );

  const handleIdentifierKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && !loading && isIdentifierValid) {
        handleSendOTP();
      }
    },
    [handleSendOTP, isIdentifierValid, loading]
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: '#2C6CA0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 2, sm: 3, md: 4 },
      }}
    >
      <Container 
        maxWidth={isDesktop ? 'sm' : isTablet ? 'md' : 'xs'}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center', 
        }}
      >
        <Paper
          elevation={isMobile ? 2 : 8}
          sx={{
            p: { 
              xs: 3, 
              sm: 4, 
              md: 5, 
              lg: 6 
            },
            width: '100%',
            maxWidth: {
              xs: '100%',
              sm: 450,
              md: 500,
              lg: 550,
            },
            borderRadius: {
              xs: 2,
              sm: 3,
              md: 4,
            },
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }}
        >
          <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 3, md: 4 } }}>
            <Box sx={{ mb: { xs: 2, sm: 3 } }}>
              <img 
                src="/logo.png" 
                alt="LottoReco Admin Logo" 
                style={{ 
                  width: isMobile ? 80 : isTablet ? 100 : 120, 
                  height: 'auto',
                  marginBottom: '16px'
                }} 
              />
            </Box>
            <Typography 
              variant={isMobile ? 'h5' : isTablet ? 'h4' : 'h3'} 
              component="h1" 
              gutterBottom
              sx={{
                fontWeight: 700,
                background: '#2C6CA0',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: { xs: 1, sm: 2 },
              }}
            >
              Welcome to LottoReco Admin
            </Typography>
            <Typography 
              variant={isMobile ? 'body2' : 'body1'} 
              color="text.secondary"
              sx={{ 
                fontSize: { xs: '0.875rem', sm: '1rem' },
                lineHeight: 1.5,
              }}
            >
              Sign in to access the LottoReco Admin
            </Typography>
          </Box>

          <Stepper 
            activeStep={activeStep} 
            sx={{ 
              mb: { xs: 3, sm: 4, md: 5 },
              '& .MuiStepLabel-root': {
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
              },
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel 
                  sx={{
                    '& .MuiStepLabel-label': {
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      fontWeight: 500,
                    },
                  }}
                >
                  {isMobile ? label.split(' ')[0] : label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: { xs: 2, sm: 3 },
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
              }}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert 
              severity="success" 
              sx={{ 
                mb: { xs: 2, sm: 3 },
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
              }}
            >
              {success}
            </Alert>
          )}

          {activeStep === 0 && (
            <Box>
              <ToggleButtonGroup
                fullWidth
                exclusive
                size={isMobile ? 'small' : 'medium'}
                value={loginMethod}
                onChange={handleLoginMethodChange}
                aria-label="OTP delivery method"
                sx={{
                  mb: { xs: 2, sm: 3 },
                  '& .MuiToggleButton-root': {
                    py: { xs: 1, sm: 1.25 },
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    fontWeight: 600,
                    textTransform: 'none',
                    color: '#2C6CA0',
                    borderColor: 'rgba(44, 108, 160, 0.35)',
                    '&.Mui-selected': {
                      color: '#fff',
                      backgroundColor: '#2C6CA0',
                      '&:hover': {
                        backgroundColor: '#255f8d',
                      },
                    },
                  },
                }}
              >
                <ToggleButton value="email" aria-label="Send OTP by email">
                  <Email sx={{ mr: 0.75, fontSize: 18 }} />
                  Email
                </ToggleButton>
                <ToggleButton value="sms" aria-label="Send OTP by text message">
                  <Sms sx={{ mr: 0.75, fontSize: 18 }} />
                  Text
                </ToggleButton>
                <ToggleButton value="voice" aria-label="Send OTP by phone call">
                  <PhoneInTalk sx={{ mr: 0.75, fontSize: 18 }} />
                  Call
                </ToggleButton>
              </ToggleButtonGroup>
              <TextField
                fullWidth
                size="small"
                label={identifierLabel}
                value={identifier}
                onChange={handleIdentifierChange}
                onKeyDown={handleIdentifierKeyDown}
                placeholder={identifierPlaceholder}
                type={loginMethod === 'email' ? 'email' : 'tel'}
                error={identifier.length > 0 && !isIdentifierValid}
                helperText={
                  identifier.length > 0 && !isIdentifierValid
                    ? invalidIdentifierMessage
                    : `Enter your ${loginMethod === 'email' ? 'email address' : 'phone number'} to receive OTP by ${deliveryLabel}`
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {loginMethod === 'email' ? (
                        <Email sx={{ color: 'text.secondary' }} />
                      ) : loginMethod === 'voice' ? (
                        <PhoneInTalk sx={{ color: 'text.secondary' }} />
                      ) : (
                        <Phone sx={{ color: 'text.secondary' }} />
                      )}
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  mb: { xs: 2, sm: 3 },
                  '& .MuiInputBase-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    height: { xs: 48, sm: 56 },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  },
                  '& .MuiFormHelperText-root': {
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  },
                }}
              />
              <Button
                fullWidth
                variant="contained"
                onClick={handleSendOTP}
                disabled={loading || !isIdentifierValid}
                startIcon={loading ? <CircularProgress size={20} /> : <Lock />}
                size={isMobile ? 'medium' : 'large'}
                sx={{
                  height: { xs: 48, sm: 56 },
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  fontWeight: 600,
                  borderRadius: { xs: 2, sm: 3 },
                  background: '#2C6CA0',
                  '&:hover': {
                    background: '#2C6CA0',
                  },
                }}
              >
                {loading ? 'Sending OTP...' : 'Send OTP'}
              </Button>
            </Box>
          )}

          {activeStep === 1 && (
            <Box>
              <TextField
                fullWidth
                size="small"
                label="OTP"
                value={otp}
                onChange={handleOTPChange}
                onKeyDown={handleOtpKeyDown}
                placeholder="123456"
                type="text"
                inputProps={{ 
                  maxLength: 6,
                  pattern: '[0-9]*',
                  inputMode: 'numeric'
                }}
                error={otp.length > 0 && !isOTPValid}
                helperText={
                  otp.length > 0 && !isOTPValid
                    ? 'Please enter exactly 6 digits'
                    : `Enter the 6-digit code sent by ${deliveryLabel}`
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  mb: { xs: 2, sm: 3 },
                  '& .MuiInputBase-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    height: { xs: 48, sm: 56 },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  },
                  '& .MuiFormHelperText-root': {
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  },
                }}
              />
              <Button
                fullWidth
                variant="contained"
                onClick={handleVerifyOTP}
                disabled={loading || !isOTPValid}
                startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
                size={isMobile ? 'medium' : 'large'}
                sx={{
                  height: { xs: 48, sm: 56 },
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  fontWeight: 600,
                  borderRadius: { xs: 2, sm: 3 },
                  mb: { xs: 2, sm: 3 },
                  background: '#2C6CA0',
                  '&:hover': {
                    background: '#2C6CA0',
                  },
                }}
              >
                {loading ? 'Verifying...' : 'Login'}
              </Button>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setActiveStep(0);
                  setOtp('');
                  setError('');
                  setSuccess('');
                }}
                disabled={loading}
                size={isMobile ? 'medium' : 'large'}
                sx={{
                  height: { xs: 48, sm: 56 },
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  fontWeight: 600,
                  borderRadius: { xs: 2, sm: 3 },
                  borderColor: '#2C6CA0',
                  color: '#2C6CA0',
                  '&:hover': {
                    borderColor: '#667eea',
                    backgroundColor: 'rgba(102, 126, 234, 0.04)',
                  },
                }}
              >
                Back
              </Button>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default Login; 
